'use client';

import { useEffect, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { api } from '@/api/client';
import { ProfileContext } from '@/contexts/ProfileContext';
import { useAuth } from '@/hooks/useAuth';
import {
  LOGGED_OUT_PROFILE_SNAPSHOT,
  deriveUserProfileSnapshot,
  type UserProfileSnapshot,
} from '@/lib/profile';

interface ProfileProviderProps {
  /**
   * Profile snapshot derived from the `user` cookie during SSR (see
   * app/layout.tsx). The server HTML and the client's first render both use
   * cookie-derived state, so personalized UI (name, MR-employee subtabs) is
   * correct on the very first paint with no flicker and no hydration
   * mismatch.
   *
   * Optional so statically-served shells (no server, no request cookies —
   * e.g. private routes served directly by nginx) keep working: without it,
   * the profile is derived from document.cookie on the client.
   */
  initialProfile?: UserProfileSnapshot;
  children: React.ReactNode;
}

export const ProfileProvider = ({ initialProfile, children }: ProfileProviderProps) => {
  const { isAuthenticated } = useAuth();
  const [cookies] = useCookies(['user']);

  // Same rationale as AuthProvider: react-cookie reads document.cookie
  // synchronously on the client, so live derivation is already correct on
  // first render; during SSR there's no request context, so fall back to the
  // snapshot the layout derived from the request cookies.
  const snapshot: UserProfileSnapshot =
    typeof window === 'undefined'
      ? (initialProfile ?? LOGGED_OUT_PROFILE_SNAPSHOT)
      : deriveUserProfileSnapshot({ user: cookies.user });

  // The API sets the `user` cookie as a side effect of GET /profile/user. If
  // an authenticated user doesn't have it yet (e.g. it hasn't been issued, or
  // was cleared), take one shot at fetching it so the cookie gets populated —
  // react-cookie polls document.cookie, so the snapshot above updates once it
  // lands.
  const hasRequestedUserCookie = useRef(false);
  const userCookieRetryCount = useRef(0);
  const MAX_USER_COOKIE_RETRIES = 3;

  useEffect(() => {
    if (
      !isAuthenticated ||
      cookies.user ||
      hasRequestedUserCookie.current ||
      userCookieRetryCount.current >= MAX_USER_COOKIE_RETRIES
    ) {
      return;
    }

    hasRequestedUserCookie.current = true;
    api.get('/profile/user').catch(() => {
      userCookieRetryCount.current += 1;
      hasRequestedUserCookie.current = false;
    });
  }, [isAuthenticated, cookies.user]);

  return <ProfileContext.Provider value={snapshot}>{children}</ProfileContext.Provider>;
};
